import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface ColoredDropdownOption<T = string> {
  value: T;
  label: string;
}

interface ColoredDropdownProps<T = string> {
  value: T;
  onChange: (value: T) => void;
  options: ColoredDropdownOption<T>[];
  disabled?: boolean;
  className?: string;
  hasChanged?: boolean;
}

// Internal color mapping based on option index
const COLOR_SCHEMES = [
  {
    background: "bg-green-100 text-green-700",
    hover:
      "hover:bg-green-300 focus:bg-green-300 hover:text-green-700 focus:text-green-700",
  },
  {
    background: "bg-yellow-100 text-yellow-700",
    hover:
      "hover:bg-yellow-300 focus:bg-yellow-300 hover:text-yellow-700 focus:text-yellow-700",
  },
  {
    background: "bg-red-100 text-red-700",
    hover:
      "hover:bg-red-300 focus:bg-red-300 hover:text-red-700 focus:text-red-700",
  },
];

export function ColoredDropdown<T extends string = string>({
  value,
  onChange,
  options,
  disabled,
  className,
  hasChanged = false,
}: ColoredDropdownProps<T>) {
  const selectedIndex = options.findIndex((opt) => opt.value === value);
  const selectedOption = options[selectedIndex];
  const selectedColors = COLOR_SCHEMES[selectedIndex] || COLOR_SCHEMES[0];

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className={cn(
          "w-full rounded-none border-none focus:ring-0 focus:ring-offset-0 font-medium",
          selectedColors.background,
          className
        )}
      >
        <SelectValue>
          <span className={hasChanged ? "font-bold" : ""}>
            {selectedOption?.label}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-background">
        {options.map((option, index) => {
          const colors = COLOR_SCHEMES[index] || COLOR_SCHEMES[0];
          return (
            <SelectItem
              key={option.value}
              value={option.value}
              className={cn(`cursor-pointer`, colors.background, colors.hover)}
            >
              {option.label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
